import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { serverTimestamp } from 'firebase/firestore';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { useAppPinLock } from '@/contexts/AppPinLockContext';
import { useMyInviteToken } from '@/contexts/ChatCryptoBootstrapContext';
import { useChatInboxUnread } from '@/contexts/ChatInboxUnreadContext';
import { p2pThreadId } from '@/services/p2pThreadInvitePath';
import {
  subscribeCallSessions,
  updateCallSession,
  type CallKind,
} from '@/services/webrtcCallFirestore';
import { startIncomingRingingSound, stopIncomingRingingSound } from '@/services/callSounds';
import { isFreshIncomingCallSession, isLiveOutgoingRingingSession } from '@/services/callSessionFreshness';
import { attachMediaPlaybackUnlockOnGesture } from '@/services/unlockMediaPlayback';
import { PhoneIncoming, PhoneOff, Video } from 'lucide-react';
import { useTranslation } from 'react-i18next';

type GlobalIncomingCall = {
  threadId: string;
  sessionId: string;
  peerInviteToken: string;
  peerName: string;
  peerPhotoURL: string | null;
  kind: CallKind;
};

type GlobalOutgoingCall = {
  threadId: string;
  sessionId: string;
  peerInviteToken: string;
  peerName: string;
  peerPhotoURL: string | null;
  kind: CallKind;
};

export function GlobalIncomingCallBanner() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useAuth();
  const { blocksCalls } = useAppPinLock();
  const uid = currentUser?.uid ?? '';
  const myToken = useMyInviteToken();
  const { contacts, peerProfiles, peerProfilesLoadedByToken } = useChatInboxUnread();
  const [globalIncomingCall, setGlobalIncomingCall] = useState<GlobalIncomingCall | null>(null);
  const [globalOutgoingCall, setGlobalOutgoingCall] = useState<GlobalOutgoingCall | null>(null);
  const [bannerEntered, setBannerEntered] = useState(false);
  const peerProfilesRef = useRef(peerProfiles);
  peerProfilesRef.current = peerProfiles;
  const peerLoadedRef = useRef(peerProfilesLoadedByToken);
  peerLoadedRef.current = peerProfilesLoadedByToken;

  const isAppArea = location.pathname.startsWith('/app');
  const callsAvailable = isAppArea && !blocksCalls;

  useEffect(() => {
    if (isAppArea) attachMediaPlaybackUnlockOnGesture();
  }, [isAppArea]);

  useEffect(() => {
    if (!callsAvailable || !uid || !myToken || contacts.length === 0) return;
    const unsubs: Array<() => void> = [];
    let cancelled = false;

    for (const c of contacts) {
      if (!c.id || c.id === myToken) continue;
      const threadId = p2pThreadId(myToken, c.id);
      const unsub = subscribeCallSessions(
        threadId,
        (list) => {
          if (cancelled) return;
          const incoming = list.find(
            ({ data }) =>
              data.state === 'ringing' &&
              data.targetUid === uid &&
              data.initiatorUid !== uid &&
              Boolean(data.offerSdp) &&
              isFreshIncomingCallSession(data)
          );

          if (incoming) {
            const profiles = peerProfilesRef.current;
            const loaded = peerLoadedRef.current;
            const displayName =
              (loaded[c.id] ? profiles[c.id]?.displayName?.trim() : '') ||
              c.name ||
              t('chats.contactNameLoading');
            setGlobalIncomingCall((prev) => {
              if (prev?.sessionId === incoming.id) return prev;
              return {
                threadId,
                sessionId: incoming.id,
                peerInviteToken: c.id,
                peerName: displayName,
                peerPhotoURL: profiles[c.id]?.photoURL ?? null,
                kind: incoming.data.kind,
              };
            });
            return;
          }

          const outgoing = list.find(({ data }) => isLiveOutgoingRingingSession(data, uid));

          if (outgoing) {
            const profiles = peerProfilesRef.current;
            const loaded = peerLoadedRef.current;
            const displayName =
              (loaded[c.id] ? profiles[c.id]?.displayName?.trim() : '') ||
              c.name ||
              t('chats.contactNameLoading');
            setGlobalOutgoingCall((prev) => {
              if (prev?.sessionId === outgoing.id) return prev;
              return {
                threadId,
                sessionId: outgoing.id,
                peerInviteToken: c.id,
                peerName: displayName,
                peerPhotoURL: profiles[c.id]?.photoURL ?? null,
                kind: outgoing.data.kind,
              };
            });
          } else {
            setGlobalOutgoingCall((prev) => {
              if (!prev || prev.threadId !== threadId) return prev;
              const sameSession = list.find(({ id }) => id === prev.sessionId);
              if (sameSession && sameSession.data.state === 'ringing') return prev;
              return null;
            });
          }

          setGlobalIncomingCall((prev) => {
            if (!prev || prev.threadId !== threadId) return prev;
            const sameSession = list.find(({ id }) => id === prev.sessionId);
            if (sameSession && sameSession.data.state === 'ringing') return prev;
            stopIncomingRingingSound();
            return null;
          });
        },
        () => undefined
      );
      unsubs.push(unsub);
    }

    return () => {
      cancelled = true;
      for (const u of unsubs) u();
    };
  }, [contacts, callsAvailable, myToken, t, uid]);

  useEffect(() => {
    if (callsAvailable) return;
    setGlobalIncomingCall(null);
    setGlobalOutgoingCall(null);
    stopIncomingRingingSound();
  }, [callsAvailable]);

  useEffect(() => {
    const sid = globalIncomingCall?.sessionId ?? null;
    if (!callsAvailable || !sid) {
      stopIncomingRingingSound();
      return;
    }
    const generation = startIncomingRingingSound();
    return () => {
      // Defer so React Strict Mode remount can start the same loop without a cut/restart.
      queueMicrotask(() => stopIncomingRingingSound(generation));
    };
  }, [globalIncomingCall?.sessionId, callsAvailable]);

  useEffect(() => {
    if (!globalIncomingCall && !globalOutgoingCall) return;
    setBannerEntered(false);
    const timer = window.setTimeout(() => {
      setBannerEntered(true);
    }, 24);
    return () => {
      window.clearTimeout(timer);
    };
  }, [globalIncomingCall?.sessionId, globalOutgoingCall?.sessionId]);

  if (!callsAvailable || (!globalIncomingCall && !globalOutgoingCall)) return null;

  const activeIncoming = globalIncomingCall;
  const activeOutgoing = !activeIncoming ? globalOutgoingCall : null;

  return (
    <div className="pointer-events-none fixed left-1/2 top-[max(0.5rem,env(safe-area-inset-top,0px))] z-[120] w-full max-w-xl -translate-x-1/2 px-3 sm:px-4 md:left-[calc(50%+8rem)]">
      <div
        className={`pointer-events-auto w-full rounded-b-lg rounded-t-none border border-white/10 bg-[hsl(var(--sidebar-background))] px-3 py-2.5 text-white shadow-lg backdrop-blur transition-all duration-300 ease-out ${
          bannerEntered
            ? 'translate-y-0 scale-100 opacity-100'
            : '-translate-y-3 scale-[0.985] opacity-0'
        }`}
      >
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex min-w-0 flex-1 items-center gap-2.5">
            <div className="relative h-9 w-9 shrink-0 overflow-hidden rounded-full">
              {(activeIncoming?.peerPhotoURL ?? activeOutgoing?.peerPhotoURL) ? (
                <img
                  src={(activeIncoming?.peerPhotoURL ?? activeOutgoing?.peerPhotoURL) as string}
                  alt=""
                  className="h-full w-full object-cover"
                  referrerPolicy="no-referrer"
                />
              ) : (
                <div className="flex h-full w-full items-center justify-center bg-white/10 text-[11px] font-semibold text-white">
                  {(activeIncoming?.peerName || activeOutgoing?.peerName || '?').slice(0, 2).toUpperCase()}
                </div>
              )}
            </div>
            <div className="min-w-0 text-left">
              <p className="flex items-center gap-1.5 text-sm font-medium leading-none text-white">
                <PhoneIncoming className="h-3.5 w-3.5 shrink-0" aria-hidden />
                {activeIncoming ? t('calls.incomingTitle') : t('calls.outgoingCalling', { name: activeOutgoing?.peerName ?? '' })}
              </p>
              {activeIncoming ? (
                <p className="truncate text-sm text-white/80">
                  {activeIncoming.kind === 'video'
                    ? t('calls.incomingDescriptionVideo', { name: activeIncoming.peerName })
                    : t('calls.incomingDescription', { name: activeIncoming.peerName })}
                </p>
              ) : null}
            </div>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <Button
              type="button"
              variant="destructive"
              size="sm"
              className="gap-1.5 bg-destructive/90 hover:bg-destructive"
              onClick={() => {
                const call = activeIncoming ?? activeOutgoing;
                if (activeIncoming) stopIncomingRingingSound();
                if (activeIncoming) setGlobalIncomingCall(null);
                if (activeOutgoing) setGlobalOutgoingCall(null);
                if (!call) return;
                void updateCallSession(call.threadId, call.sessionId, {
                  state: activeIncoming ? 'rejected' : 'ended',
                  endedAt: serverTimestamp(),
                }).catch(() => undefined);
              }}
            >
              <PhoneOff className="h-3.5 w-3.5" aria-hidden />
              {activeIncoming ? t('calls.reject') : t('calls.cancel')}
            </Button>
            {activeIncoming ? (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                className="gap-1.5 border border-emerald-500/40 bg-emerald-600 text-emerald-50 hover:bg-emerald-500"
                onClick={() => {
                  stopIncomingRingingSound();
                  navigate(
                    `/app/chats/${encodeURIComponent(activeIncoming.peerInviteToken)}?acceptCall=${encodeURIComponent(activeIncoming.sessionId)}`
                  );
                }}
              >
                <Video className="h-3.5 w-3.5" aria-hidden />
                {t('calls.accept')}
              </Button>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
